import { annualSoftwarePrice, formatSoftwareEuro } from '../lib/softwarePricing';
import { Form, reportFormError } from '../components/ui/validated-form';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, ChevronRight, ChevronLeft } from 'lucide-react';
import { getUserErrorMessage, plansAPI } from '../lib/api';
import DataProcessingAgreementDialog from '../components/DataProcessingAgreementDialog';

const PLAN_SUMMARY = {
  starter: { label: 'Starter', yearly: '129 EUR / Monat bei jährlicher Zahlung (1548 EUR / Jahr)' },
  growth: { label: 'Business', yearly: '199 EUR / Monat bei jährlicher Zahlung (2388 EUR / Jahr)' },
  pro: { label: 'Pro', yearly: '299 EUR / Monat bei jährlicher Zahlung (3588 EUR / Jahr)' },
};

function getPlanDisplayText(planKey, billingPeriod, planSummary = PLAN_SUMMARY) {
  const p = planSummary[planKey];
  if (!p) return planKey;
  return `${p.label} – ${p.yearly}`;
}

export default function Register() {
  // Only the contract-backed AnnualRegistration may create new organizations.
  // The legacy form is retained for local migration checks, never production.
  if (process.env.NODE_ENV === 'production') return <RegistrationUnavailable />;
  return <LegacyRegister />;
}

export function RegistrationUnavailable() {
  return <main className="flex min-h-screen items-center justify-center bg-[#F5F5F7] px-4 py-12">
    <section className="w-full max-w-lg rounded-2xl border border-[#E5E5EA] bg-white p-8 shadow-sm">
      <h1 className="text-2xl font-semibold text-[#1D1D1F]">Registrierung wird aktualisiert</h1>
      <p className="mt-4 text-[#86868B]">Wir bereiten die neue Registrierung vor. Bitte versuche es später erneut. Dein bestehender Zugang bleibt verfügbar.</p>
      <div className="mt-6 flex flex-wrap justify-end gap-3"><Button variant="outline" asChild><Link to="/">Zur Startseite</Link></Button><Button asChild><Link to="/login">Anmelden</Link></Button></div>
    </section>
  </main>;
}

function LegacyRegister() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: '',
    business_name: '',
    billing_street: '',
    billing_postal_code: '',
    billing_city: '',
    billing_country: 'DE',
    avv_accepted: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [avvOpen, setAvvOpen] = useState(false);
  const { registerWithTrial } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedPlan, setSelectedPlan] = useState(() => {
    const raw = String(searchParams.get('plan') || 'starter').toLowerCase();
    return PLAN_SUMMARY[raw] ? raw : 'starter';
  });

  const billingPeriod = 'yearly';
  const [planSummary, setPlanSummary] = useState(PLAN_SUMMARY);

  useEffect(() => {
    let active = true;
    plansAPI.listPublic().then((response) => {
      if (!active) return;
      const next = { ...PLAN_SUMMARY };
      (response.data?.plans || []).forEach((plan) => {
        const key = String(plan.plan || '').toLowerCase();
        if (!next[key]) return;
        const price = annualSoftwarePrice(plan);
        next[key] = {
          ...next[key],
          yearly: `${formatSoftwareEuro(price.monthly)} / Monat (${formatSoftwareEuro(price.annual)} netto jährlich im Voraus)`,
        };
      });
      setPlanSummary(next);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (formData.password !== formData.password_confirm) {
      reportFormError(form, 'Die Passwörter stimmen nicht überein.', 'password_confirm');
      return;
    }

    if (formData.password.length < 6) {
      reportFormError(form, 'Bitte mindestens 6 Zeichen für das Passwort eingeben.', 'password');
      return;
    }

    if (!formData.avv_accepted) {
      reportFormError(form, 'Bitte bestätige den Auftragsverarbeitungsvertrag, um fortzufahren.', 'avv_accepted');
      return;
    }

    setLoading(true);

    try {
      const result = await registerWithTrial({
        email: formData.email,
        password: formData.password,
        org_name: formData.business_name,
        first_name: formData.first_name,
        last_name: formData.last_name,
        billing_street: formData.billing_street || undefined,
        billing_postal_code: formData.billing_postal_code || undefined,
        billing_city: formData.billing_city || undefined,
        billing_country: formData.billing_country || undefined,
        plan: selectedPlan,
        billing_period: billingPeriod,
        avv_accepted: Boolean(formData.avv_accepted),
      });

      const checkoutUrl = result?.checkout_url;
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }

      if (result?.checkout_required) {
        toast.error('Stripe-Checkout konnte nicht gestartet werden.');
        navigate('/upgrade?required=1', { replace: true });
        return;
      }

      toast.success('Konto erstellt. Bitte bestätige deine E-Mail-Adresse über den Link, den wir dir gesendet haben.');
      setFormData({ first_name: '', last_name: '', email: '', password: '', password_confirm: '', business_name: '', billing_street: '', billing_postal_code: '', billing_city: '', billing_country: 'DE', avv_accepted: false });
    } catch (error) {
      reportFormError(form, getUserErrorMessage(error, 'Registrierung fehlgeschlagen. Bitte erneut versuchen.'), error?.response?.data?.errors?.[0]?.field);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-[#F5F5F7] flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/5659012/pexels-photo-5659012.jpeg"
          alt="Professional clinic consultation"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/45 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white text-2xl font-medium leading-relaxed">
            "Die beste Entscheidung für unsere Klinik. Die intuitive Online-Buchung und Dokumentation spart uns täglich wertvolle Arbeitszeit."
          </blockquote>
          <p className="mt-4 text-white/85 font-medium">— Dr. Ricarda Granes, Hauptstadt Clinic Berlin</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 min-h-0 overflow-hidden">
        <div className="w-full max-w-md shrink-0">
          <Link to="/" className="inline-flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <div className="mb-4">
              <img
                src="/images/mib-logo-full.png"
                alt="MIB Logo"
                className="h-10 w-auto object-contain"
              />
            </div>

            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-[#1D1D1F]">Konto erstellen</h1>
              <span className="text-sm text-[#86868B]">Schritt {step} von 3</span>
            </div>

            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Abrechnungszeitraum</Label>
                  <p className="text-sm">Jährliche Vorauszahlung · 12 Monate Laufzeit</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Plan</Label>
                  <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                    <SelectTrigger className="input-base h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">{getPlanDisplayText('starter', billingPeriod, planSummary)}</SelectItem>
                      <SelectItem value="growth">{getPlanDisplayText('growth', billingPeriod, planSummary)}</SelectItem>
                      <SelectItem value="pro">{getPlanDisplayText('pro', billingPeriod, planSummary)}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-[#86868B]">
                  Die Zahlung erfolgt sicher über Stripe. Dein kostenpflichtiges Abonnement beginnt mit dem Abschluss des Checkouts.
                </p>
                <Button
                  type="button"
                  className="btn-primary w-full"
                  onClick={() => setStep(2)}
                  data-testid="register-step1-next"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name" className="text-[#1D1D1F]">Unternehmensname <span className="text-red-500">*</span></Label>
                  <Input
                    id="business_name"
                    name="business_name"
                    placeholder="Mein Salon"
                    value={formData.business_name}
                    onChange={handleChange}
                    className="input-base"
                    required
                    data-testid="register-business"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_street" className="text-[#1D1D1F]">Straße und Hausnummer <span className="text-red-500">*</span></Label>
                  <Input
                    id="billing_street"
                    name="billing_street"
                    placeholder="Musterstraße 1"
                    value={formData.billing_street}
                    onChange={handleChange}
                    className="input-base"
                    required
                    data-testid="register-billing-street"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="billing_postal_code" className="text-[#1D1D1F]">PLZ <span className="text-red-500">*</span></Label>
                    <Input inputKind="postal"
                      id="billing_postal_code"
                      name="billing_postal_code"
                      placeholder="12345"
                      value={formData.billing_postal_code}
                      onChange={handleChange}
                      className="input-base"
                      required
                      data-testid="register-billing-postal-code"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="billing_city" className="text-[#1D1D1F]">Stadt <span className="text-red-500">*</span></Label>
                    <Input
                      id="billing_city"
                      name="billing_city"
                      placeholder="Berlin"
                      value={formData.billing_city}
                      onChange={handleChange}
                      className="input-base"
                      required
                      data-testid="register-billing-city"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_country" className="text-[#1D1D1F]">Land <span className="text-red-500">*</span></Label>
                  <Select value={formData.billing_country} onValueChange={(v) => setFormData((p) => ({ ...p, billing_country: v }))}>
                    <SelectTrigger className="input-base h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DE">Deutschland</SelectItem>
                      <SelectItem value="AT">Österreich</SelectItem>
                      <SelectItem value="CH">Schweiz</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setStep(1)}
                    disabled={loading}
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                  <Button
                    type="button"
                    className="btn-primary flex-1"
                    onClick={() => {
                      if (!formData.business_name?.trim()) {
                        toast.error('Bitte gib den Unternehmensnamen ein');
                        return;
                      }
                      if (!formData.billing_street?.trim() || !formData.billing_postal_code?.trim() || !formData.billing_city?.trim()) {
                        toast.error('Bitte gib die vollständige Rechnungsadresse ein');
                        return;
                      }
                      setStep(3);
                    }}
                    data-testid="register-step2-next"
                  >
                    Weiter
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <Form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-[#1D1D1F]">Vorname <span className="text-red-500">*</span></Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      placeholder="Max"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="input-base"
                      required
                      data-testid="register-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-[#1D1D1F]">Nachname <span className="text-red-500">*</span></Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      placeholder="Mustermann"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="input-base"
                      required
                      data-testid="register-lastname"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1D1D1F]">E-Mail <span className="text-red-500">*</span></Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@beispiel.de"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-base"
                    required
                    data-testid="register-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1D1D1F]">Passwort <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="********"
                      value={formData.password}
                      onChange={handleChange}
                      className="input-base pr-10"
                      required
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-[#86868B]">Mindestens 6 Zeichen</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password_confirm" className="text-[#1D1D1F]">Passwort bestätigen <span className="text-red-500">*</span></Label>
                  <Input
                    id="password_confirm"
                    name="password_confirm"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="********"
                    value={formData.password_confirm}
                    onChange={handleChange}
                    className="input-base"
                    required
                    data-testid="register-password-confirm"
                  />
                </div>

                <label className="flex items-start gap-3 rounded-2xl border border-[#E5E5EA] p-4 text-sm text-[#1D1D1F]">
                  <Checkbox
                    checked={Boolean(formData.avv_accepted)}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, avv_accepted: Boolean(checked) }))}
                  />
                  <span className="leading-6">
                    Ich schließe den{' '}
                    <button
                      type="button"
                      className="font-medium text-[#0071E3] underline underline-offset-2"
                      onClick={() => setAvvOpen(true)}
                    >
                      Auftragsverarbeitungsvertrag (AVV)
                    </button>{' '}
                    ab. <span className="text-red-500">*</span>
                  </span>
                </label>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setStep(2)}
                    disabled={loading}
                    data-testid="register-step2-back"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                  <Button
                    type="submit"
                    className="btn-primary flex-1"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        Wird erstellt...
                      </span>
                    ) : (
                      'Konto erstellen'
                    )}
                  </Button>
                </div>
              </Form>
            )}

            <p className="mt-4 text-center text-[#86868B] text-xs">
              Mit der Registrierung akzeptierst du unsere{' '}
              <Link to="/nutzungsbedingungen" className="text-[#0071E3] hover:underline">AGB</Link>
              {' '}und{' '}
              <Link to="/datenschutz" className="text-[#0071E3] hover:underline">Datenschutzrichtlinie</Link>.
            </p>

            <p className="mt-3 text-center text-[#86868B] text-xs">
              <span className="text-red-500">*</span> Pflichtfelder
            </p>
            <p className="mt-3 text-center text-[#86868B] text-sm">
              Bereits registriert{' '}
              <Link to="/login" className="text-[#0071E3] hover:underline font-medium">
                Anmelden
              </Link>
            </p>
          </div>
        </div>
      </div>
      <DataProcessingAgreementDialog
        open={avvOpen}
        onOpenChange={setAvvOpen}
        customerName={formData.business_name || 'Organisation laut Registrierung'}
      />
    </div>
  );
}
